import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ContentInterpreterService {
    /**
     * Decodes a Base64 string to a UTF‑8 string in a Unicode-safe way.
     */
    private decodeBase64Unicode(base64: string): string {
        try {
            // atob is not Unicode-safe, so use a Unicode-safe routine:
            return decodeURIComponent(
                Array.prototype.map
                    .call(atob(base64), (c: string) =>
                        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                    )
                    .join('')
            );
        } catch (error) {
            console.error('Error decoding Base64 Unicode string', error);
            return '';
        }
    }

    /**
     * Interprets Base64-encoded content based on the content type.
     * @param contentType MIME type (e.g., "application/json", "text/markdown", "image/png")
     * @param base64Data Base64 string
     * @returns For JSON: parsed object; for text: decoded string; for images/audio/video: Data URL.
     */
    interpretContent<T>(contentType: string, base64Data: string): T | string | null {
        if (!base64Data) {
            console.warn('No Base64 data provided');
            return null;
        }

        const lowerContentType = contentType.toLowerCase();

        if (lowerContentType.includes('json')) {
            try {
                const jsonStr = this.decodeBase64Unicode(base64Data);
                return JSON.parse(jsonStr) as T;
            } catch (error) {
                console.error('Error parsing JSON content', error);
                return null;
            }
        }

        if (lowerContentType.includes('text') || lowerContentType.includes('markdown')) {
            return this.decodeBase64Unicode(base64Data);
        }

        if (lowerContentType.startsWith('image') ||
            lowerContentType.startsWith('audio') ||
            lowerContentType.startsWith('video')) {
            // Return a Data URL that can be used directly in the src attribute.
            return `data:${contentType};base64,${base64Data}`;
        }

        // Fallback: just return the Base64 string.
        return base64Data;
    }
}
